import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Refresh } from "@mui/icons-material";
import { callBackendApi } from "../../methods/axiosInstances/backendInstance/apiRequest";
import ResourceCrudPanel from "../../components/crm/ResourceCrudPanel";
import {
  CrmCheckboxInput,
  CrmFormGrid,
  CrmNumberInput,
  CrmSelectInput,
  CrmTextInput,
} from "../../components/crm/CrudFormControls";

const asText = (value) => String(value ?? "").trim();
const asNullableText = (value) => {
  const next = asText(value);
  return next === "" ? null : next;
};
const asNullable = (value) => (value === "" || value === undefined ? null : value);
const asNumber = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};
const asNullableNumber = (value) => {
  if (value === "" || value === undefined || value === null) return null;
  return asNumber(value);
};

const selectSource = (name, endpoint, labelFields, valueField = "id") => ({
  name,
  optionSource: { endpoint, valueField, labelFields },
});

function InventoryCategoriesForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="parent_id"
        label="Parent Category"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.parent_id || []}
        nullable
      />
      <CrmTextInput
        name="name"
        label="Name"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function InventoryItemsForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="status"
        label="Status"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ]}
      />
      <CrmTextInput
        name="sku"
        label="SKU"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="name"
        label="Name"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="description"
        label="Description"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmSelectInput
        name="category_id"
        label="Category"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.category_id || []}
        nullable
      />
      <CrmTextInput
        name="unit"
        label="Unit"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmCheckboxInput
        name="is_sellable"
        label="Sellable"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmCheckboxInput
        name="is_stock_tracked"
        label="Stock Tracked"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmSelectInput
        name="vat_rate_id"
        label="VAT Rate"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.vat_rate_id || []}
        nullable
      />
      <CrmNumberInput
        name="stock_on_hand"
        label="Stock On Hand"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="stock_min"
        label="Stock Minimum"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="avg_cost"
        label="Average Cost"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function InventoryStockLocationsForm({ formData, setFormData, errors, clearFieldError }) {
  return (
    <CrmFormGrid>
      <CrmTextInput
        name="code"
        label="Code"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="name"
        label="Name"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmCheckboxInput
        name="is_active"
        label="Active"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function InventoryStockMovementsForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="inventory_item_id"
        label="Inventory Item"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.inventory_item_id || []}
      />
      <CrmSelectInput
        name="location_id"
        label="Stock Location"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.location_id || []}
      />
      <CrmSelectInput
        name="movement_type"
        label="Movement Type"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "purchase_in", label: "Purchase In" },
          { value: "sale_out", label: "Sale Out" },
          { value: "sale_return_in", label: "Sale Return In" },
          { value: "adjustment", label: "Adjustment" },
          { value: "internal_use_out", label: "Internal Use Out" },
        ]}
      />
      <CrmNumberInput
        name="qty"
        label="Quantity"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="unit_cost"
        label="Unit Cost"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="reference_type"
        label="Reference Type"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmSelectInput
        name="reference_id"
        label="Reference"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.reference_id || []}
        nullable
      />
      <CrmTextInput
        name="reason"
        label="Reason"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmSelectInput
        name="created_by_user_id"
        label="Created By User"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.created_by_user_id || []}
        nullable
      />
    </CrmFormGrid>
  );
}

function SuppliersForm({ formData, setFormData, errors, clearFieldError }) {
  return (
    <CrmFormGrid>
      <CrmTextInput
        name="name"
        label="Name"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="vat_number"
        label="VAT Number"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="email"
        label="Email"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="phone"
        label="Phone"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="address"
        label="Address"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="notes"
        label="Notes"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function PurchaseOrdersForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="supplier_id"
        label="Supplier"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.supplier_id || []}
      />
      <CrmSelectInput
        name="status"
        label="Status"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "draft", label: "Draft" },
          { value: "received", label: "Received" },
          { value: "cancelled", label: "Cancelled" },
        ]}
      />
      <CrmTextInput
        name="ordered_at"
        label="Ordered At (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="received_at"
        label="Received At (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="notes"
        label="Notes"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmSelectInput
        name="created_by_user_id"
        label="Created By User"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.created_by_user_id || []}
        nullable
      />
    </CrmFormGrid>
  );
}

function PurchaseOrderLinesForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="purchase_order_id"
        label="Purchase Order"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.purchase_order_id || []}
      />
      <CrmSelectInput
        name="inventory_item_id"
        label="Inventory Item"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.inventory_item_id || []}
      />
      <CrmNumberInput
        name="qty"
        label="Quantity"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="unit_cost"
        label="Unit Cost"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmSelectInput
        name="vat_rate_id"
        label="VAT Rate"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.vat_rate_id || []}
        nullable
      />
    </CrmFormGrid>
  );
}

const inventoryResources = [
  {
    key: "inventory-categories",
    label: "Inventory Categories",
    endpoint: "/inventory-categories",
    selectSources: [
      selectSource("parent_id", "/inventory-categories", ["name", "uuid"]),
    ],
    renderForm: InventoryCategoriesForm,
    toPayload: (formData) => ({
      parent_id: asNullable(formData.parent_id),
      name: asText(formData.name),
    }),
  },
  {
    key: "inventory-items",
    label: "Inventory Items",
    endpoint: "/inventory-items",
    relatedTabs: [
      {
        key: "inventory-stock-movements",
        label: "Stock Movements",
        endpoint: "/inventory-stock-movements",
        foreignKey: "inventory_item_id",
      },
      {
        key: "purchase-order-lines",
        label: "Purchase Order Lines",
        endpoint: "/purchase-order-lines",
        foreignKey: "inventory_item_id",
      },
      {
        key: "inventory-stock-locations",
        label: "Stock Locations",
        endpoint: "/inventory-stock-locations",
        fromTab: "inventory-stock-movements",
        fromField: "location_id",
        toField: "id",
      },
      {
        key: "purchase-orders",
        label: "Purchase Orders",
        endpoint: "/purchase-orders",
        fromTab: "purchase-order-lines",
        fromField: "purchase_order_id",
        toField: "id",
      },
      {
        key: "suppliers",
        label: "Suppliers",
        endpoint: "/suppliers",
        fromTab: "purchase-orders",
        fromField: "supplier_id",
        toField: "id",
      },
    ],
    tableFields: [
      { name: "status", label: "Status" },
      { name: "sku", label: "SKU" },
      { name: "name", label: "Name" },
      { name: "unit", label: "Unit" },
      { name: "stock_on_hand", label: "Stock On Hand" },
      { name: "stock_min", label: "Stock Min" },
      { name: "avg_cost", label: "Average Cost" },
      { name: "is_stock_tracked", label: "Stock Tracked" },
      { name: "is_sellable", label: "Sellable" },
    ],
    selectSources: [
      selectSource("category_id", "/inventory-categories", ["name", "uuid"]),
      selectSource("vat_rate_id", "/vat-rates", ["code", "name"]),
    ],
    renderForm: InventoryItemsForm,
    toPayload: (formData) => ({
      status: asText(formData.status),
      sku: asText(formData.sku),
      name: asText(formData.name),
      description: asNullableText(formData.description),
      category_id: asNullable(formData.category_id),
      unit: asText(formData.unit),
      is_sellable: Boolean(formData.is_sellable),
      is_stock_tracked: Boolean(formData.is_stock_tracked),
      vat_rate_id: asNullable(formData.vat_rate_id),
      stock_on_hand: asNumber(formData.stock_on_hand),
      stock_min: asNumber(formData.stock_min),
      avg_cost: asNumber(formData.avg_cost),
    }),
  },
  {
    key: "inventory-stock-locations",
    label: "Stock Locations",
    endpoint: "/inventory-stock-locations",
    relatedTabs: [
      {
        key: "inventory-stock-movements",
        label: "Stock Movements",
        endpoint: "/inventory-stock-movements",
        foreignKey: "location_id",
      },
    ],
    renderForm: InventoryStockLocationsForm,
    toPayload: (formData) => ({
      code: asText(formData.code),
      name: asText(formData.name),
      is_active: Boolean(formData.is_active),
    }),
  },
  {
    key: "inventory-stock-movements",
    label: "Stock Movements",
    endpoint: "/inventory-stock-movements",
    tableFields: [
      {
        name: "inventory_item_id",
        label: "Inventory Item",
        valueGetter: (row) => row?.inventory_item_name || row?.inventory_item?.name || row?.inventory_item_id,
      },
      {
        name: "location_id",
        label: "Location",
        valueGetter: (row) => row?.location_name || row?.location?.name || row?.location_id,
      },
      { name: "movement_type", label: "Movement Type" },
      { name: "qty", label: "Quantity" },
      { name: "unit_cost", label: "Unit Cost" },
      { name: "reference_type", label: "Reference Type" },
      {
        name: "reference_id",
        label: "Reference",
        valueGetter: (row) => row?.reference_label || row?.reference_purchase_order?.uuid || row?.reference_id,
      },
      { name: "reason", label: "Reason" },
      {
        name: "created_by_user_id",
        label: "Created By",
        valueGetter: (row) => row?.created_by_user_name || row?.created_by_user?.name || row?.created_by_user?.email || row?.created_by_user_id,
      },
      { name: "created_at", label: "Created At" },
    ],
    selectSources: [
      selectSource("inventory_item_id", "/inventory-items", ["name", "sku", "uuid"]),
      selectSource("location_id", "/inventory-stock-locations", ["name", "code", "uuid"]),
      selectSource("reference_id", "/purchase-orders", ["uuid", "status", "ordered_at"]),
      selectSource("created_by_user_id", "/users", ["name", "email"]),
    ],
    renderForm: InventoryStockMovementsForm,
    toPayload: (formData) => ({
      inventory_item_id: asNullable(formData.inventory_item_id),
      location_id: asNullable(formData.location_id),
      movement_type: asText(formData.movement_type),
      qty: asNumber(formData.qty),
      unit_cost: asNullableNumber(formData.unit_cost),
      reference_type: asNullableText(formData.reference_type),
      reference_id: asNullable(formData.reference_id),
      reason: asNullableText(formData.reason),
      created_by_user_id: asNullable(formData.created_by_user_id),
    }),
  },
  {
    key: "suppliers",
    label: "Suppliers",
    endpoint: "/suppliers",
    relatedTabs: [
      {
        key: "purchase-orders",
        label: "Purchase Orders",
        endpoint: "/purchase-orders",
        foreignKey: "supplier_id",
      },
    ],
    renderForm: SuppliersForm,
    toPayload: (formData) => ({
      name: asText(formData.name),
      vat_number: asNullableText(formData.vat_number),
      email: asNullableText(formData.email),
      phone: asNullableText(formData.phone),
      address: asNullableText(formData.address),
      notes: asNullableText(formData.notes),
    }),
  },
  {
    key: "purchase-orders",
    label: "Purchase Orders",
    endpoint: "/purchase-orders",
    tableFields: [
      {
        name: "supplier_id",
        label: "Supplier",
        valueGetter: (row) => row?.supplier_name || row?.supplier?.name || row?.supplier_id,
      },
      { name: "status", label: "Status" },
      { name: "ordered_at", label: "Ordered At" },
      { name: "received_at", label: "Received At" },
      { name: "notes", label: "Notes" },
      {
        name: "created_by_user_id",
        label: "Created By",
        valueGetter: (row) => row?.created_by_user_name || row?.created_by_user?.name || row?.created_by_user?.email || row?.created_by_user_id,
      },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    relatedTabs: [
      {
        key: "purchase-order-lines",
        label: "Order Lines",
        endpoint: "/purchase-order-lines",
        foreignKey: "purchase_order_id",
      },
    ],
    selectSources: [
      selectSource("supplier_id", "/suppliers", ["name", "uuid"]),
      selectSource("created_by_user_id", "/users", ["name", "email"]),
    ],
    renderForm: PurchaseOrdersForm,
    toPayload: (formData) => ({
      supplier_id: asNullable(formData.supplier_id),
      status: asText(formData.status),
      ordered_at: asNullableText(formData.ordered_at),
      received_at: asNullableText(formData.received_at),
      notes: asNullableText(formData.notes),
      created_by_user_id: asNullable(formData.created_by_user_id),
    }),
  },
  {
    key: "purchase-order-lines",
    label: "Purchase Order Lines",
    endpoint: "/purchase-order-lines",
    tableFields: [
      {
        name: "purchase_order_id",
        label: "Purchase Order",
        valueGetter: (row) => row?.purchase_order_label || row?.purchase_order?.id || row?.purchase_order_id,
      },
      {
        name: "inventory_item_id",
        label: "Inventory Item",
        valueGetter: (row) => row?.inventory_item_name || row?.inventory_item?.name || row?.inventory_item_id,
      },
      { name: "qty", label: "Quantity" },
      { name: "unit_cost", label: "Unit Cost" },
      {
        name: "vat_rate_id",
        label: "VAT Rate",
        valueGetter: (row) => row?.vat_rate_label || row?.vat_rate?.code || row?.vat_rate_id,
      },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    selectSources: [
      selectSource("purchase_order_id", "/purchase-orders", ["uuid", "status", "ordered_at"]),
      selectSource("inventory_item_id", "/inventory-items", ["name", "sku", "uuid"]),
      selectSource("vat_rate_id", "/vat-rates", ["code", "name"]),
    ],
    renderForm: PurchaseOrderLinesForm,
    toPayload: (formData) => ({
      purchase_order_id: asNullable(formData.purchase_order_id),
      inventory_item_id: asNullable(formData.inventory_item_id),
      qty: asNumber(formData.qty),
      unit_cost: asNumber(formData.unit_cost),
      vat_rate_id: asNullable(formData.vat_rate_id),
    }),
  },
];

const normalizeRows = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && typeof data === "object") return [data];
  return [];
};

const buildColumns = (rows, explicitFields = []) => {
  const keys = new Set();
  explicitFields.forEach((field) => {
    if (field?.name) {
      keys.add(field.name);
    }
  });

  rows.slice(0, 30).forEach((row) => {
    if (!row || typeof row !== "object") return;
    Object.keys(row).forEach((key) => {
      if (key !== "id") {
        keys.add(key);
      }
    });
  });

  const labels = Object.fromEntries(
    explicitFields
      .filter((field) => field?.name)
      .map((field) => [field.name, field.label || field.name])
  );

  return Array.from(keys).slice(0, 10).map((fieldName) => ({
    field: fieldName,
    headerName: labels[fieldName] || fieldName,
    flex: fieldName === "id" ? undefined : 1,
    width: fieldName === "id" ? 100 : undefined,
    valueGetter: (...args) => {
      const row = args?.[1] ?? args?.[0]?.row;
      const value = row?.[fieldName];
      if (value === undefined || value === null) return "";
      if (typeof value === "object") return JSON.stringify(value);
      return value;
    },
  }));
};

function RelationTable({ title, rows }) {
  const columns = React.useMemo(() => buildColumns(rows), [rows]);

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>{title}</Typography>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row, index) => row.id ?? row.uuid ?? `${title}-${index}`}
          pageSizeOptions={[5, 10, 20]}
          initialState={{ pagination: { paginationModel: { pageSize: 5, page: 0 } } }}
          autoHeight
        />
      </CardContent>
    </Card>
  );
}

export default function InventoryPage() {
  const resources = inventoryResources;
  const resourceByKey = React.useMemo(
    () => Object.fromEntries(resources.map((resource) => [resource.key, resource])),
    [resources]
  );

  const [mainTab, setMainTab] = React.useState(0);
  const [hubTab, setHubTab] = React.useState(0);

  const [loading, setLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const [items, setItems] = React.useState([]);
  const [stockMovements, setStockMovements] = React.useState([]);
  const [stockLocations, setStockLocations] = React.useState([]);
  const [suppliers, setSuppliers] = React.useState([]);
  const [purchaseOrders, setPurchaseOrders] = React.useState([]);
  const [purchaseOrderLines, setPurchaseOrderLines] = React.useState([]);
  const [selectedItemId, setSelectedItemId] = React.useState(null);

  const itemFields = React.useMemo(
    () => resourceByKey["inventory-items"]?.tableFields || [],
    [resourceByKey]
  );

  const loadInventoryHubData = React.useCallback(async () => {
    setLoading(true);
    setErrorMessage("");

    const [
      itemsResponse,
      movementsResponse,
      locationsResponse,
      suppliersResponse,
      poResponse,
      poLinesResponse,
    ] = await Promise.all([
      callBackendApi("/inventory-items", "GET"),
      callBackendApi("/inventory-stock-movements", "GET"),
      callBackendApi("/inventory-stock-locations", "GET"),
      callBackendApi("/suppliers", "GET"),
      callBackendApi("/purchase-orders", "GET"),
      callBackendApi("/purchase-order-lines", "GET"),
    ]);

    if (
      !itemsResponse?.success ||
      !movementsResponse?.success ||
      !locationsResponse?.success ||
      !suppliersResponse?.success ||
      !poResponse?.success ||
      !poLinesResponse?.success
    ) {
      setErrorMessage("Failed to load inventory related data.");
      setLoading(false);
      return;
    }

    const itemRows = normalizeRows(itemsResponse.data);
    setItems(itemRows);
    setStockMovements(normalizeRows(movementsResponse.data));
    setStockLocations(normalizeRows(locationsResponse.data));
    setSuppliers(normalizeRows(suppliersResponse.data));
    setPurchaseOrders(normalizeRows(poResponse.data));
    setPurchaseOrderLines(normalizeRows(poLinesResponse.data));

    if (!selectedItemId && itemRows.length > 0) {
      setSelectedItemId(itemRows[0].id);
    }

    if (selectedItemId && !itemRows.some((row) => row.id === selectedItemId)) {
      setSelectedItemId(itemRows[0]?.id ?? null);
    }

    setLoading(false);
  }, [selectedItemId]);

  React.useEffect(() => {
    if (mainTab === 0) {
      loadInventoryHubData();
    }
  }, [mainTab, loadInventoryHubData]);

  const selectedItem = React.useMemo(
    () => items.find((item) => item.id === selectedItemId) || null,
    [items, selectedItemId]
  );

  const relatedMovements = React.useMemo(
    () => stockMovements.filter((movement) => movement.inventory_item_id === selectedItemId),
    [stockMovements, selectedItemId]
  );

  const relatedPoLines = React.useMemo(
    () => purchaseOrderLines.filter((line) => line.inventory_item_id === selectedItemId),
    [purchaseOrderLines, selectedItemId]
  );

  const relatedPoIds = React.useMemo(
    () => [...new Set(relatedPoLines.map((line) => line.purchase_order_id).filter(Boolean))],
    [relatedPoLines]
  );

  const relatedPurchaseOrders = React.useMemo(
    () => purchaseOrders.filter((order) => relatedPoIds.includes(order.id)),
    [purchaseOrders, relatedPoIds]
  );

  const relatedSupplierIds = React.useMemo(
    () => [...new Set(relatedPurchaseOrders.map((order) => order.supplier_id).filter(Boolean))],
    [relatedPurchaseOrders]
  );

  const relatedSuppliers = React.useMemo(
    () => suppliers.filter((supplier) => relatedSupplierIds.includes(supplier.id)),
    [suppliers, relatedSupplierIds]
  );

  const relatedLocationIds = React.useMemo(
    () => [...new Set(relatedMovements.map((movement) => movement.location_id).filter(Boolean))],
    [relatedMovements]
  );

  const relatedLocations = React.useMemo(
    () => stockLocations.filter((location) => relatedLocationIds.includes(location.id)),
    [stockLocations, relatedLocationIds]
  );

  const managementTabs = [
    { label: "Inventory Hub", resource: null },
    { label: "Items", resource: resourceByKey["inventory-items"] },
    { label: "Stock Locations", resource: resourceByKey["inventory-stock-locations"] },
    { label: "Stock Movements", resource: resourceByKey["inventory-stock-movements"] },
    { label: "Purchase Orders", resource: resourceByKey["purchase-orders"] },
    { label: "Purchase Order Lines", resource: resourceByKey["purchase-order-lines"] },
    { label: "Suppliers", resource: resourceByKey.suppliers },
    { label: "Categories", resource: resourceByKey["inventory-categories"] },
  ];

  return (
    <Box sx={{ width: "100%", maxWidth: "1700px", margin: "auto" }}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Box>
              <Typography variant="h5">Inventory</Typography>
              <Typography variant="body2" color="text.secondary">
                Grouped by category: hub with related data plus separate management tabs.
              </Typography>
            </Box>
            {mainTab === 0 ? (
              <Button variant="outlined" startIcon={<Refresh />} onClick={loadInventoryHubData}>
                Refresh Hub
              </Button>
            ) : null}
          </Stack>

          <Tabs value={mainTab} onChange={(_, value) => setMainTab(value)} variant="scrollable" scrollButtons="auto">
            {managementTabs.map((tab) => (
              <Tab key={tab.label} label={tab.label} />
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {mainTab === 0 ? (
        <Stack spacing={2}>
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Select Inventory Item
              </Typography>
              <DataGrid
                rows={items}
                columns={buildColumns(items, itemFields)}
                loading={loading}
                getRowId={(row, index) => row.id ?? row.uuid ?? `item-${index}`}
                pageSizeOptions={[5, 10, 20]}
                initialState={{ pagination: { paginationModel: { pageSize: 5, page: 0 } } }}
                onRowClick={(params) => setSelectedItemId(params.row.id)}
                autoHeight
              />
            </CardContent>
          </Card>

          {selectedItem ? (
            <Card>
              <CardContent>
                <Typography variant="subtitle1">
                  Selected: {selectedItem.name} (ID: {selectedItem.id})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  SKU: {selectedItem.sku} | Unit: {selectedItem.unit} | Stock: {selectedItem.stock_on_hand}
                </Typography>
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardContent sx={{ pb: 0 }}>
              <Tabs value={hubTab} onChange={(_, value) => setHubTab(value)} variant="scrollable" scrollButtons="auto">
                <Tab label="Stock Movements" />
                <Tab label="Stock Locations" />
                <Tab label="PO Lines" />
                <Tab label="Purchase Orders" />
                <Tab label="Suppliers" />
              </Tabs>
            </CardContent>
          </Card>

          {hubTab === 0 ? <RelationTable title="Stock Movements" rows={relatedMovements} /> : null}
          {hubTab === 1 ? <RelationTable title="Stock Locations" rows={relatedLocations} /> : null}
          {hubTab === 2 ? <RelationTable title="Purchase Order Lines" rows={relatedPoLines} /> : null}
          {hubTab === 3 ? <RelationTable title="Purchase Orders" rows={relatedPurchaseOrders} /> : null}
          {hubTab === 4 ? <RelationTable title="Suppliers" rows={relatedSuppliers} /> : null}
        </Stack>
      ) : (
        <ResourceCrudPanel
          resource={managementTabs[mainTab]?.resource}
          resourceLookup={resourceByKey}
        />
      )}
    </Box>
  );
}
